/* eslint-disable no-nested-ternary */
/* eslint-disable react/forbid-prop-types */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DownArrow, InfoIcon } from 'components/CommonIcons';
import Tooltip from '../Tooltip';
import DropdownItem from './DropdownItem';
import * as styles from './Dropdown.scss';
import Searchbar from '../Searchbar';

/**
 * @class Dropdown
 * @classdesc Reusable Dropdown Component
 * @property {Object} state
 * @property {Object} dropdownRef
 * @example ./Dropdown.md
 */
export default class Dropdown extends PureComponent {
  static propTypes = {
    displayValue: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    list: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string,
      ]),
    ).isRequired,
    onClick: PropTypes.func.isRequired,
    arrowClassName: PropTypes.string,
    buttonClassName: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    downStateClassName: PropTypes.string,
    /* TODO: Configure Dropdown ErrorState Logic */
    // errorState: PropTypes.bool,
    label: PropTypes.string,
    listClassName: PropTypes.string,
    reactKey: PropTypes.string,
    size: PropTypes.string,
    style: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    ),
    titleClassName: PropTypes.string,
    filterable: PropTypes.bool,
    showInfoIcon: PropTypes.bool,
    dropdownTooltipDescription: PropTypes.string,
    tooltipStyle: PropTypes.object,
    arrowPlacement: PropTypes.string,
  };

  static defaultProps = {
    arrowClassName: null,
    buttonClassName: null,
    className: null,
    downStateClassName: null,
    disabled: false,
    /* TODO: Uncomment when ErrorState Logic Configured */
    // errorState: false,
    label: null,
    listClassName: null,
    reactKey: 'text',
    size: 'LARGE',
    style: null,
    titleClassName: null,
    filterable: false,
    showInfoIcon: false,
    dropdownTooltipDescription: '',
    tooltipStyle: {},
    arrowPlacement: '',
  };

  constructor(props) {
    super(props);
    const { displayValue } = this.props;

    this.state = {
      displayValue,
      searchValue: '',
      open: false,
      isTooltipVisible: false,
    };
    this.dropdownRef = React.createRef();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.displayValue !== prevState.displayValue) return { displayValue: nextProps.displayValue };
    return null;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  /**
   * @description Verifies if click locations are outside of component or not
   * @param {Object} - Event object
   * @return {(Function|null)} - return handleClose or nothing based on click location
   */
  handleClickOutside = e => {
    if (this.dropdownRef && !this.dropdownRef.current.contains(e.target)) return this.handleClose();
    return null;
  };

  /**
   * Closes dropdown menu
   * @return {Function} this.setState()
   */
  handleClose = () => {
    return this.setState({ open: false });
  };

  /**
   * Click handler for selecting list items in menu
   * @param {Object}
   * @return {Function} this.props.onClick
   */
  handleSelection = item => {
    const { disabled, onClick } = this.props;

    this.setState({ open: false });
    return disabled
      ? null
      : onClick(item.text, item.value, item);
  };

  /**
   * @description Toggles opening/closing dropdown menu when dropdown clicked
   */
  toggleList = () => {
    this.setState(prevState => ({ open: !prevState.open, searchValue: '' }));
  };

  toggleTooltip = () => {
    this.setState(prevState => ({ isTooltipVisible: !prevState.isTooltipVisible }));
  };

  renderLabel = label => {
    const { showInfoIcon, dropdownTooltipDescription, tooltipStyle, arrowPlacement } = this.props;
    if (showInfoIcon) {
      const { isTooltipVisible } = this.state;
      return (
        <p
          onMouseEnter={this.toggleTooltip}
          onMouseLeave={this.toggleTooltip}
          className={styles.label}>{label}
          <Tooltip
            hidden={!isTooltipVisible}
            arrowPlacement={arrowPlacement}
            style={tooltipStyle}
          >
            <div>
              {`${dropdownTooltipDescription}`}
            </div>
          </Tooltip>
          <span style={{ marginLeft: '8px' }}><InfoIcon /></span>
        </p>);
    }
    return <p className={styles.label}>{label}</p>;
  };

  /**
   * @description Renders dropdown with text
   * @return {ReactElement}
   */
  renderDropdownButton = () => {
    const {
      arrowClassName,
      buttonClassName,
      disabled,
      downStateClassName,
      size,
      titleClassName
    } = this.props;
    const { displayValue, open } = this.state;

    const defaultButtonClass = size === 'LARGE'
      ? styles.dropdownBtnLarge
      : styles.dropdownBtn;
    const defaultTitleClass = size === 'LARGE'
      ? styles.dropdownTitleLarge
      : styles.dropdownTitle;

    return (
      <div
        className={classNames(
          defaultButtonClass,
          { [styles.dropdownBtnOpen]: open },
          { [styles.dropdownBtnDisabled]: disabled },
          buttonClassName,
          { [downStateClassName]: open },
        )}
        onClick={disabled ? null : this.toggleList}
      >
        <p className={classNames(
          defaultTitleClass,
          titleClassName,
          { [styles.dropdownTitleDisabled]: disabled },
        )}
        >
          {displayValue}
        </p>
        <div className={classNames(
          styles.arrow,
          { [styles.arrowLarge]: size === 'LARGE' },
          { [styles.arrowDisabled]: disabled },
          arrowClassName
        )}>
          <DownArrow />
        </div>
      </div>
    );
  };

  /**
   * @description Search throw dropdown elements
   * @return {ReactElement}
   */
  handleSearch = (value) => {
    this.setState({ searchValue: value });
  };

  /**
   * @description Renders dropdown list when dropdown state open is true
   * @return {ReactElement}
   */
  renderDropdownList = () => {
    const { list, listClassName, reactKey, size, filterable } = this.props;
    const large = size === 'LARGE';

    const { searchValue } = this.state;
    const filteredItems = filterable && searchValue
      ? list.filter(x => x.text.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1)
      : list;

    return (
      <div className={styles.dropdownContainer}>
        {filterable ? (
          <div className={styles.filterSection}>
            <Searchbar
              inputClassName={styles.attributeSearch}
              onChange={this.handleSearch}
              value={searchValue} />
          </div>)
          : null}
        <ul className={classNames(
          styles.dropdownList,
          { [styles.listLarge]: large },
          listClassName
        )}>
          {filteredItems.map(item => {
            return (
              <DropdownItem
                key={item[reactKey]}
                className={item.className}
                dangerBtn={item.dangerBtn}
                disabled={item.disabled}
                onClick={() => this.handleSelection(item)}
                text={item.text}
                style={item.style}
                successBtn={item.successBtn}
                value={item.value}
                title={item.title} />
            );
          })}
        </ul>
      </div>
    );
  };

  render() {
    const { className, label, style, size } = this.props;
    const { open } = this.state;
    const defaultWrapperClass = size === 'LARGE'
      ? styles.wrapperLarge
      : styles.wrapper;

    return (
      <div
        ref={this.dropdownRef}
        className={classNames(defaultWrapperClass, className)}
        style={style}
      >
        {label && this.renderLabel(label)}
        {this.renderDropdownButton()}
        {open && this.renderDropdownList()}
      </div>
    );
  }
}
